"use client";

import { Tag, Folder } from "lucide-react";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square bg-muted/30 animate-pulse rounded-lg" />
        <div className="space-y-6">
          <div className="h-10 w-3/4 bg-muted/30 animate-pulse rounded-lg" />
          <div className="h-8 w-1/3 bg-muted/30 animate-pulse rounded-lg" />
          <div className="h-24 w-full bg-muted/30 animate-pulse rounded-lg" />
          <div className="h-12 w-full bg-muted/30 animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground mb-4">Failed to load product.</p>
        <button onClick={refetch} className="underline text-blue-600">Retry</button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Product not found.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount = product.discounted_price !== undefined && product.discounted_price !== null && product.discounted_price < product.price;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
      {}
      <div className="relative">
        <div className="relative border-2 border-blue-600/40 bg-white shadow-lg rounded-lg overflow-hidden">
          {}
          <div className="absolute top-0 left-0 w-12 h-12 border-r-2 border-b-2 border-blue-600 z-10 pointer-events-none" />
          <div className="absolute top-0 right-0 w-12 h-12 border-l-2 border-b-2 border-blue-600 z-10 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-12 h-12 border-r-2 border-t-2 border-blue-600 z-10 pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-l-2 border-t-2 border-blue-600 z-10 pointer-events-none" />

          {product.featured_image_url ? (
            <img
              src={product.featured_image_url}
              alt={product.title}
              className="w-full aspect-square object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full aspect-square bg-slate-100 flex items-center justify-center">
              <span className="text-slate-500">No image</span>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs font-sans uppercase tracking-widest text-slate-500">
          <span className="inline-block w-8 h-px bg-blue-600" />
          <span>Item ID {String(product.id).padStart(4, "0")}</span>
        </div>
      </div>

      {}
      <div className="flex flex-col">
        <div className="mb-2 flex items-center gap-2 text-xs font-sans uppercase tracking-widest text-blue-600">
          <span className="inline-block w-2 h-2 bg-blue-600" />
          <span>DTFPRINT / Apparel Transfer</span>
        </div>

        <h1 className="font-default text-3xl md:text-5xl font-bold uppercase tracking-tight leading-tight text-slate-900">
          {product.title}
        </h1>

        <Separator className="my-6 bg-blue-600/40" />

        {}
        <div className="flex items-baseline gap-4 mb-6">
          {hasDiscount ? (
            <>
              <span className="font-default text-4xl font-bold text-blue-600">
                ${product.discounted_price?.toFixed(2)}
              </span>
              <span className="text-xl text-slate-500 line-through font-sans">
                ${product.price.toFixed(2)}
              </span>
              <Badge variant="default" className="bg-blue-600 text-white font-sans uppercase rounded-lg">
                Sale
              </Badge>
            </>
          ) : (
            <span className="font-default text-4xl font-bold text-blue-600">
              ${product.price?.toFixed(2)}
            </span>
          )}
        </div>

        {}
        {product.short_description && (
          <div
            className="text-base text-slate-700 leading-relaxed mb-8 [&_p]:mb-2 [&_p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        {}
        <div className="border-t-2 border-b-2 border-blue-600/40 py-6 mb-8">
          <AddToCartButton
            productId={product.id}
            className="w-full"
            buttonClassName="bg-blue-600 text-white font-default font-bold uppercase tracking-widest text-base py-6 rounded-lg"
            text="Add to Cart"
            showQuantity={true}
          />
        </div>

        {}
        <div className="space-y-4">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-2 min-w-[110px] text-xs font-sans uppercase tracking-widest text-slate-500 pt-1">
                <Folder className="w-3.5 h-3.5" />
                <span>Categories</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.categories.map((cat, i) => (
                  <Link
                    to={cat.link || "#"}
                    key={cat.id}
                    data-index={i}
                  >
                    <Badge
                      variant="outline"
                      className="border-blue-600/50 text-slate-900 hover:bg-blue-600 hover:text-white font-sans uppercase text-xs rounded-lg"
                    >
                      {cat.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-2 min-w-[110px] text-xs font-sans uppercase tracking-widest text-slate-500 pt-1">
                <Tag className="w-3.5 h-3.5" />
                <span>Tags</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, i) => (
                  <Link
                    to={tag.link || "#"}
                    key={tag.id}
                    data-index={i}
                  >
                    <Badge
                      variant="secondary"
                      className="bg-slate-100 text-slate-700 hover:bg-blue-600 hover:text-white font-sans uppercase text-xs rounded-lg"
                    >
                      #{tag.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      {product.long_description && (
        <div className="lg:col-span-2 mt-8 lg:mt-12">
          <div className="border-t-2 border-blue-600/40 pt-10">
            <div className="mb-6 flex items-center gap-3">
              <span className="inline-block w-3 h-3 bg-blue-600" />
              <h2 className="font-default text-2xl md:text-3xl font-bold uppercase tracking-tight text-slate-900">
                Technical Specifications
              </h2>
              <span className="flex-1 h-px bg-blue-600/30" />
            </div>
            <div
              className="prose prose-slate max-w-none text-slate-700 leading-relaxed [&_p]:mb-4 [&_h2]:font-default [&_h2]:uppercase [&_h2]:tracking-tight [&_h3]:font-default [&_h3]:uppercase [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Product() {
  const current_wp_query_params = typeof window !== "undefined" ? window.wvcClient?.getCurrentWPQueryParams?.() : {};
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section data-section-id="1564"
      id="single-product-template-section"
      className="bg-slate-50 text-slate-900 py-16 md:py-24 px-4 md:px-8 relative overflow-hidden"
    >
      {}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, var(--primary) 0, var(--primary) 1px, transparent 0, transparent 16px), repeating-linear-gradient(-45deg, var(--primary) 0, var(--primary) 1px, transparent 0, transparent 16px)`,
        }}
      />

      <div className="relative max-w-7xl mx-auto">
        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}